/**
 * A tab left open across a deploy still runs the old build. The first time it
 * needs a lazily loaded part of a page (the agent editor, a tab's panel), it
 * asks for a script file the new build no longer has, and the page fell to
 * "Something went wrong" with a Try Again button that could never work.
 *
 * The cure is a reload, which picks up the current build. It is done once per
 * path in a short window, so a file that is truly missing shows the error page
 * instead of reloading forever.
 */
const KEY = 'uzo-stale-build-reload';
const WINDOW_MS = 60_000;

export function isStaleBuildError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  const text = `${e?.name || ''} ${e?.message || ''}`;
  return /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module/i.test(text);
}

/** Reloads the page when the error is a stale build. True when a reload was started. */
export function reloadIfStaleBuild(error: unknown): boolean {
  if (typeof window === 'undefined' || !isStaleBuildError(error)) return false;
  try {
    const last = JSON.parse(window.sessionStorage.getItem(KEY) || 'null') as { path?: string; at?: number } | null;
    if (last && last.path === window.location.pathname && Date.now() - (last.at || 0) < WINDOW_MS) return false;
    window.sessionStorage.setItem(KEY, JSON.stringify({ path: window.location.pathname, at: Date.now() }));
  } catch {
    // Without session storage the loop guard cannot be kept, so leave the error page up.
    return false;
  }
  window.location.reload();
  return true;
}
